import ccfImageWithZoomStyles from './ccf-image-with-zoom.style';
import { useTheme, useMediaQuery, Theme } from '@mui/material';
import { CcfCloseIcon, CcfPlusIcon, CcfMinusIcon } from '../../../index';
import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Button } from '@mui/material';

export interface CcfImageWithZoomProps {
  imgUrl: string,
  imgAltText: string,
  isModalOpen: boolean,
  closeModal: () => void,
  isInboxCollapsed?: boolean
}

/**
 * Component displays image adaptive cards with zoom capability
 * @param props - CcfImageWithZoomProps
 * @returns image expanded with zoom options 
 * @example `<CcfImageWithZoom imgUrl={imgUrl} imgAltText={imgAltText} isModalOpen={isModalOpen} closeModal={closeModal} isInboxCollapsed={true} />`
 */
export function CcfImageWithZoom(
  { imgUrl, imgAltText, isModalOpen, closeModal, isInboxCollapsed = false }: CcfImageWithZoomProps) {
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const cardStyles = ccfImageWithZoomStyles(theme);
  const [imageTransformScale, setImageTransformScale] = useState(1);

  /**
   * Method to check if further zoom in of attachment possible in preview mode
   * ```
   * @example
   * zoomInPossible()
   * />
   *
   * ```
   */
  const zoomInPossible = () => {
    if (imageTransformScale < (window.innerWidth < 425 ? 1.2 : 1.05)) {
      return true;
    }
    return false;
  };

  /**
   * Method to check if further zoom out of attachment possible in preview mode
   * ```
   * @example
   * zoomOutPossible()
   * />
   *
   * ```
   */
  const zoomOutPossible = () => {
    if (imageTransformScale > 0.8) {
      return true;
    }
    return false;
  };

  /**
   * Method to zoom in to increase the size of attachment in preview
   * ```
   * @example
   * handleZoomIn()
   * />
   *
   * ```
   */
  const handleZoomIn = () => {
    if (zoomInPossible()) {
      setImageTransformScale(imageTransformScale * 1.02);
    }
  };

  /**
   * Method to zoom out to decrease the size of attachment in preview
   * ```
   * @example
   * handleZoomOut()
   * />
   *
   * ```
   */
  const handleZoomOut = () => {
    if (zoomOutPossible()) {
      setImageTransformScale(imageTransformScale / 1.02);
    }
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={closeModal}
      aria-labelledby="expanded-image-title"
      sx={{
        ...cardStyles.expandedDialog,
        ...((isInboxCollapsed && !isBelowMd)
          ? cardStyles.widthInputCollapsed
          : isBelowMd
            ? cardStyles.widthBelowMd
            : {}),
      }}
    >
      <CcfCloseIcon sx={cardStyles.closeDialog} onClick={closeModal} />
      <DialogContent sx={cardStyles.dialogImage}>
        <img
          src={imgUrl}
          alt={imgAltText}
          style={{
            transform: `scale(${imageTransformScale})`,
            maxWidth: '100%',
            maxHeight: '100%',
            padding: '1.25rem',
          }}
        />
      </DialogContent>
      <DialogActions sx={cardStyles.dialogActions}>
        <div style={cardStyles.dialogButtons}>
          <Button onClick={() => handleZoomOut()}>
            <CcfMinusIcon sx={cardStyles.zoomOutButton} />
          </Button>
          <Button onClick={() => handleZoomIn()}>
            <CcfPlusIcon sx={cardStyles.zoomInButton} />
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default CcfImageWithZoom;
